// Packages
import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import algoliasearch from "algoliasearch/lite"
import { InstantSearch } from "react-instantsearch-dom"
import { Logo } from "@abmg-components"
import { GatsbyImage } from "gatsby-plugin-image"

// Components
import ClientSideOnly from "@components/global/client-side-only"
import Navigation from "@components/global/navigation"
import Modal from "@components/global/modal-team-apply"

// Hooks
import useClientSideOnly from "@hooks/use-client-side-only"

// -Footers
import DefaultFooter from "@components/global/Footer"
import ASAFooter from "@components/asa/footer"
import LEPFooter from "@components/global/footer-lep"
import AlumSponsorFooter from "@components/alumSponsor/footer"
import WalledFooter from "@components/global/footer-walled"
import WorksFooter from "@components/works/footer"
import DisasterReliefFooter from "@components/disaster-relief/footer"

// Utils
import { bankersWithWebsites } from "@utils/mb"

// Stores
import { useBankerStore } from "@store/useBankerStore"
import { getUrlWithParams } from "@utils/utils"
import Nav from "./Nav"

// Styles
const PageWrapper = styled.div`
  position: relative;
`

// Functions
const getClonedChildren = (children, props) =>
  React.cloneElement(children, {
    ...props,
  })

function LayoutContent(props) {
  const queryParams = new URLSearchParams(props.location.search)
  const queryParamLanguage =
    queryParams.get("lang") === "esp" ? "spanish" : "english"
  const isMounted = useClientSideOnly()

  let bankerData = useBankerStore(state =>
    state.getBanker({ ...props, isMounted }),
  )

  let { type, disclosure, version, category } = props.pageContext

  let pageName =
    props.pageContext.pageName || props.location.pathname.replace(/\//g, "")

  const [searchBoxFocus, setSearchBoxFocus] = useState(false)
  const [language, setLanguage] = useState(queryParamLanguage)
  const [teamApplyToggle, setTeamApplyToggle] = useState(false)
  const [currentLocation, setCurrentLocation] = useState(props.location)
  const [navIsOpen, setNavOpen] = useState(false)

  const [navDropdownsOpen, setNavDropdownsOpen] = useState({
    clientTools: false,
    resources: false,
  })
  const [urlSearchString, setUrlSearchString] = React.useState("")

  const mainRef = useRef(null)

  // Flag to open team apply window (for eSig team apply links)
  useEffect(() => {
    props.location.search &&
      props.location.search.includes("team-apply") &&
      bankerData?.team &&
      setTeamApplyToggle(true)
    setUrlSearchString(props.location.search)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Close KC search box when navigating to another page
  useEffect(() => {
    setSearchBoxFocus(searchBoxFocus => {
      if (searchBoxFocus) return false
      else return searchBoxFocus
    })
    setCurrentLocation(props.location)
  }, [props.location, setNavOpen, navIsOpen])

  useEffect(() => {
    setNavOpen(navIsOpen => {
      if (navIsOpen) return false
      else return navIsOpen
    })

    // Update language preference when the route changes
    if (language !== queryParamLanguage) setLanguage(queryParamLanguage)
  }, [currentLocation])

  useEffect(() => {
    if (searchBoxFocus) {
      document.querySelector(".layout").scrollIntoView()
    }
  }, [searchBoxFocus])

  const pageType =
    bankerData && isMounted
      ? "mb"
      : type === "walled"
        ? version
        : type !== "corporate" && type !== "mb"
          ? "corporate"
          : type
  let defaultNavProps = {
    bankerData,
    navIsOpen,
    pathname: props.location.pathname,
    setNavOpen,
    variants: {
      useSearch: true,
      type: pageType,
    },
  }

  let defaultFooterProps = {
    disclosure,
    bankerData,
    variants: {
      useAffiliationsBanner: true,
      type: pageType,
    },
  }

  const otherTypePageDefaultNavVariants = {
    useSearch: false,
    type: bankerData && isMounted ? "mb-other" : "other",
  }

  const otherTypePageDefaultFooterVariants = {
    useAffiliationsBanner: false,
    type: bankerData && isMounted ? "mb-other" : "other",
  }

  const lepNavBase = {
    cta: {
      text: language === "spanish" ? "English" : "En Español",
      to: getUrlWithParams({
        path: `/tu-hogar-tu-futuro/`,
        location: typeof window !== "undefined" ? window.location : undefined,
        queryParamsToAdd: props.language === "spanish" ? null : { lang: "esp" },
        queryParamsToDelete: language === "spanish" ? { lang: "esp" } : null,
      }),
    },
  }

  const otherTypePages = {
    advantage: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        variants: otherTypePageDefaultNavVariants,
      },
    },
    "disaster-relief": {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        variants: otherTypePageDefaultNavVariants,
      },
    },
    "joint-venture": {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        base: {
          cta: {
            text: `Connect with Us`,
            to: `${props.location.search}#form`,
          },
          // links: [
          //   {
          //     text: `Newsroom`,
          //     to: `/newsroom/`,
          //   },
          // ],
        },
        mobile: {
          ctaLinks: [
            {
              text: `Connect with Us`,
              to: `${props.location.search}#form`,
            },
          ],
        },
        variants: otherTypePageDefaultNavVariants,
      },
    },
    "keypleaz-atlanta": {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        variants: otherTypePageDefaultNavVariants,
      },
    },
    "lender-for-life": {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        base: {
          cta: {
            to: `/purchase-welcome${urlSearchString}`,
          },
        },
        mobile: {
          ctaLinks: [
            {
              to: `/purchase-welcome${urlSearchString}`,
            },
          ],
        },
        variants: otherTypePageDefaultNavVariants,
      },
    },
    neighborschoice: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        base: {
          cta: {
            to: `${props.location.search}#form`,
          },
        },
        mobile: {
          ctaLinks: [
            {
              to: `${props.location.search}#form`,
            },
          ],
        },
        variants: otherTypePageDefaultNavVariants,
      },
    },
    "neighborschoice/thank-you": {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        base: {
          cta: {
            to: `/neighborschoice/${props.location.search}#form`,
          },
        },
        mobile: {
          ctaLinks: [
            {
              to: `/neighborschoice/${props.location.search}#form`,
            },
          ],
        },
        variants: otherTypePageDefaultNavVariants,
      },
    },
    network: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        base: {
          cta: {
            text: `Get Connected`,
            to: `#get-in-touch`,
          },
        },
        mobile: {
          ctaLinks: [
            {
              text: `Get Connected`,
              to: `#get-in-touch`,
            },
          ],
        },
        variants: {
          ...otherTypePageDefaultNavVariants,
          disableNavPinning: true,
        },
      },
    },
    odu: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        variants: otherTypePageDefaultNavVariants,
      },
    },
    relocator: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        base: {
          cta: {
            to: `${props.location.search}#form`,
          },
        },
        mobile: {
          ctaLinks: [
            {
              to: `${props.location.search}#form`,
            },
          ],
        },
        variants: otherTypePageDefaultNavVariants,
      },
    },
    "relocator/thank-you": {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        base: {
          cta: {
            to: `/relocator/${props.location.search}#form`,
          },
        },
        mobile: {
          ctaLinks: [
            {
              to: `/relocator/${props.location.search}#form`,
            },
          ],
        },
        variants: otherTypePageDefaultNavVariants,
      },
    },
    smartpath: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        base: {
          cta: {
            to: `/fth-welcome/`,
          },
        },
        mobile: {
          ctaLinks: [
            {
              to: `/fth-welcome/`,
            },
          ],
        },
        variants: otherTypePageDefaultNavVariants,
      },
    },
    wku: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        variants: otherTypePageDefaultNavVariants,
      },
    },
    works: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        variants: otherTypePageDefaultNavVariants,
      },
    },
    uva: {
      footer: {
        ...defaultFooterProps,
        variants: otherTypePageDefaultFooterVariants,
      },
      nav: {
        ...defaultNavProps,
        variants: otherTypePageDefaultNavVariants,
      },
    },
  }

  let navProps = Object.keys(otherTypePages).includes(pageName)
    ? otherTypePages[pageName].nav
    : defaultNavProps

  let footerProps = Object.keys(otherTypePages).includes(pageName)
    ? otherTypePages[pageName].footer
    : defaultFooterProps

  let allProps = {
    ...props,
    ...props.pageContext,
    bankerData,
    navIsOpen,
    navDropdownsOpen,
    navVariant: props.pageContext.navVariant,
    setNavOpen,
    pageName,
    mainRef,
    urlSearchString,
    isMounted,
  }

  // let footerProps = {
  //   disclosure,
  //   bankerData,
  //   pageName,
  //   ...props,
  //   version: props.pageContext.version,
  //   type: props.pageContext.type,
  //   mainRef,
  //   isMounted,
  //   variants: {
  //     useAffiliationsBanner: true,
  //     type: props.pageContext.type,
  //   },
  // }

  let Footer = DefaultFooter
  let searchClient = { search: () => {} } // set as empty properties so algolia requests will only run when in knowledge center

  // const noNavPages = ["disaster-relief"]
  /*************************** Alum Sponsor ***************************/
  // if (type === "alumSponsor") {
  //   Footer = AlumSponsorFooter
  //   if (pageName === "odu") {
  //     Footer = WalledFooter
  //   }
  // }

  /*************************** ASA ***************************/
  if (type === "asa" || pageName === "keypleaz-atlanta") {
    // Footer = ASAFooter
    if (type === "asa") {
      let logo = props.data?.page?.logo && (
        <div className="flex gap-[12px] items-center">
          <Logo />
          <div className="divider self-stretch"></div>
          <GatsbyImage
            alt={props.data.page.logo.description || ""}
            className="w-fit"
            // className="h-fit max-w-[103px] desktop:max-w-[155px]"
            image={props.data.page.logo.gatsbyImageData}
          />
        </div>
      )
      if (props.data?.page?.logo) {
        navProps = {
          ...navProps,
        }
      }
      navProps = {
        ...navProps,
        logo,
        variants: otherTypePageDefaultNavVariants,
      }
      footerProps = {
        ...footerProps,
        variants: otherTypePageDefaultFooterVariants,
      }
    }
  }

  /*************************** Walled/MB ***************************/
  if (type === "walled" || type === "mb") {
    allProps = {
      ...allProps,
      teamApplyToggle,
      setTeamApplyToggle,
    }
    // footerProps = {
    //   ...footerProps,
    //   params: props.params,
    // }
  }

  /*********** -LEP (Walled category)  ***********/
  if (category === "lep") {
    allProps = {
      ...allProps,
      setLanguage,
      language,
    }

    navProps = {
      ...defaultNavProps,
      base: lepNavBase,
      mobile: {
        ctaLinks: [lepNavBase.cta],
      },
      variants: otherTypePageDefaultNavVariants,
    }
    footerProps = {
      ...defaultFooterProps,
      disclosure:
        language === "english"
          ? "tu-hogar-tu-futuro"
          : "tu-hogar-tu-futuro-spanish",
      variants: otherTypePageDefaultFooterVariants,
    }
    // Footer = LEPFooter
  }

  /*********** -KC (Walled category) ***********/
  if (category === "kc") {
    allProps = {
      ...allProps,
      setSearchBoxFocus,
      searchBoxFocus,
      articleProgressBarRoot: mainRef,
    }

    navProps = {
      ...navProps,
      articleSlug: props.data?.contentfulBlogPost?.slug,
      articleCategory: props.pageContext.articleCategory,
      articleProgressBarRoot: mainRef,
      setSearchBoxFocus,
      searchBoxFocus,
      variants: {
        ...navProps.variants,
        useKcNav: true,
      },
    }
    searchClient = algoliasearch(
      process.env.GATSBY_ALGOLIA_APP_ID,
      process.env.GATSBY_ALGOLIA_SEARCH_KEY,
    )
  }
  /*********** -Newsroom (Walled category) ***********/
  if (category === "newsroom") {
    navProps = {
      ...navProps,
      series: props.pageContext.series,
      variants: {
        ...navProps.variants,
        useNewsroomNav: true,
      },
    }
  }

  if (pageName === "agenttimeline") {
    navProps = {
      ...navProps,
      variants: {
        ...navProps.variants,
        disableNavPinning: true,
      },
    }
  }
  /*********** -Works/Advantage (Walled page)  ***********/
  // if (
  //   pageName === "works" ||
  //   pageName === "advantage" ||
  //   pageName === "seller-success"
  // ) {
  //   Footer = WorksFooter
  // }
  /*********** -Disaster Relief (Corporate page) ***********/
  // if (pageName === "disaster-relief") {
  //   Footer = DisasterReliefFooter
  // }

  return props.pageContext.type === "noLayoutMb" ? (
    getClonedChildren(props.children, {
      ...allProps,
      mainRef,
    })
  ) : (
    <PageWrapper
      className={`layout [&_.main-nav]:transition-all ${
        isMounted ? "[&_.main-nav]:opacity-100" : "[&_.main-nav]:opacity-0"
      }`}
    >
      <InstantSearch searchClient={searchClient} indexName="knowledge-center">
        {/* {noNavPages.includes(pageName) ? null : <Nav {...navProps} />} */}
        <Nav {...navProps} />
        {/* {pageName?.match("404") ? (
          <ClientSideOnly
            placeholder={<div style={{ minHeight: "168px" }}></div>}
          >
            <Navigation {...allProps} />
          </ClientSideOnly>
        ) : noNavPages.includes(pageName) ? null : (
          <Navigation {...allProps} />
        )} */}

        <>
          <main ref={mainRef}>
            {getClonedChildren(props.children, {
              ...allProps,
              mainRef,
            })}
          </main>
          {pageName?.match("404") ? (
            <ClientSideOnly
              placeholder={<div style={{ minHeight: "600px" }}></div>}
            >
              <Footer {...footerProps} />
            </ClientSideOnly>
          ) : (
            <Footer {...footerProps} />
          )}
        </>
      </InstantSearch>
      {teamApplyToggle && (
        <Modal toggleModal={setTeamApplyToggle} team={bankerData?.team} />
      )}
    </PageWrapper>
  )
}
export default LayoutContent
